import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Clock, TrendingUp, TrendingDown, Briefcase, IndianRupee, Receipt,
} from 'lucide-react';
import CreateProjectModal from '../../components/workspace/project/CreateProjectModal';
import ProjectDetailModal from '../../components/workspace/project/ProjectDetailModal';
import ProjectViewModal from '../../components/workspace/project/ProjectViewModal';
import Modal from '../../components/Modal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import { SearchableSelect, MultiFilterDropdown, DateRangeFilterDropdown, DATE_PRESETS_PAST } from '../../components/workspace/filters/FilterDropdowns';

// Indian numbering: ₹ under 1 lakh shown plain, ≥1 lakh as L, ≥1 crore as Cr.
function formatValue(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`;
  return v.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Resolves a { preset, from, to } value (from DateRangeFilterDropdown) into
// literal { from, to } date strings for the API. Full calendar periods (not
// clipped to today), since this filters projects by overlap, not a deadline.
function resolveDateRange(filter) {
  if (!filter?.preset) return { from: '', to: '' };
  if (filter.preset === 'custom') return { from: filter.from || '', to: filter.to || '' };

  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (filter.preset === 'today') {
    const d = toISODate(today);
    return { from: d, to: d };
  }
  if (filter.preset === 'this_week') {
    const dayOfWeek = (today.getDay() + 6) % 7;
    const start = new Date(today); start.setDate(today.getDate() - dayOfWeek);
    const end   = new Date(start); end.setDate(start.getDate() + 6);
    return { from: toISODate(start), to: toISODate(end) };
  }
  if (filter.preset === 'this_month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: toISODate(start), to: toISODate(end) };
  }
  return { from: '', to: '' };
}

function textToFeedHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  active:    { label: 'Active',    color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',  border: '#38bdf8' },
  completed: { label: 'Completed', color: '#4ade80', bg: 'rgba(74,210,149,0.12)', border: '#4ad295' },
  overdue:   { label: 'Overdue',   color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: '#f87171' },
  near_end:  { label: 'Near End',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', border: '#fbbf24' },
  inactive:  { label: 'Inactive',  color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', border: '#94a3b8' },
  deleted:   { label: 'Deleted',   color: '#f87171', bg: 'rgba(248,113,113,0.08)', border: '#f87171' },
};

const STATUS_FILTER_OPTIONS = [
  { value: 'active',    label: 'Active',    dot: STATUS_META.active.border },
  { value: 'completed', label: 'Completed', dot: STATUS_META.completed.border },
  { value: 'overdue',   label: 'Overdue',   dot: STATUS_META.overdue.border },
  { value: 'near_end',  label: 'Near End',  dot: STATUS_META.near_end.border },
  { value: 'inactive',  label: 'Inactive',  dot: STATUS_META.inactive.border },
];

const SORT_OPTIONS = [
  { value: '',                label: 'Default (Newest)'      },
  { value: 'start_date_desc', label: 'Start Date (Latest)'   },
  { value: 'start_date_asc',  label: 'Start Date (Earliest)' },
  { value: 'end_date_desc',   label: 'End Date (Latest)'     },
  { value: 'end_date_asc',    label: 'End Date (Earliest)'   },
  { value: 'name_asc',        label: 'Name A → Z'            },
  { value: 'progress_desc',   label: 'Progress (High → Low)' },
  { value: 'progress_asc',    label: 'Progress (Low → High)' },
];

// Only meaningful for admins/owners who can actually see revenue & cost
// (fields are omitted server-side for everyone else) — appended separately
// so the base list above stays available to every role.
const FINANCE_SORT_OPTIONS = [
  { value: 'revenue_desc', label: 'Revenue (High → Low)' },
  { value: 'revenue_asc',  label: 'Revenue (Low → High)' },
  { value: 'cost_desc',    label: 'Cost (High → Low)'    },
  { value: 'cost_asc',     label: 'Cost (Low → High)'    },
  { value: 'profit_desc',  label: 'Profit (High → Low)'  },
  { value: 'profit_asc',   label: 'Profit (Low → High)'  },
];

// Project durations are usually multi-day, so "Today" isn't a useful preset here.
const PROJECT_DATE_PRESETS = DATE_PRESETS_PAST.filter((p) => p.value !== 'today');

// ── Shared Components ─────────────────────────────────────────────────────────

function ProgressBar({ pct, color, height = 7, width }) {
  const c = color || (pct === 100 ? '#4ad295' : pct >= 70 ? '#38bdf8' : pct >= 40 ? '#fbbf24' : '#f87171');
  return (
    <div style={{ background: 'var(--bg-4)', borderRadius: 99, height, overflow: 'hidden', flex: width ? '0 0 auto' : 1, width }}>
      <div style={{
        width: `${pct}%`, height: '100%',
        background: `linear-gradient(90deg, ${c}99, ${c})`,
        borderRadius: 99, transition: 'width .5s ease',
      }} />
    </div>
  );
}

// ── Money chip — compact pill used for per-row Total Cost / Total Invoice ──────
function MoneyChip({ Icon, label, value, color }) {
  const hasValue = !!value;
  const displayColor = hasValue ? color : 'var(--fg-dim)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px', borderRadius: 8, minWidth: 118,
      background: hasValue ? `${color}12` : 'var(--bg-muted)',
      border: `1px solid ${hasValue ? `${color}2a` : 'var(--border)'}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={12} color={displayColor} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--fg-dim)' }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: displayColor, marginLeft: 'auto' }}>
        {hasValue ? formatValue(value) : '—'}
      </span>
    </span>
  );
}

// ── Dashboard Stat Card ───────────────────────────────────────────────────────
// subValue (admin-only) shows that bucket's revenue total right under the
// label, instead of a separate standalone "Project Value" card.
function StatCard({ label, value, Icon, accent, subValue }) {
  const valueStr  = String(value);
  const valueSize = valueStr.length > 10 ? 15 : valueStr.length > 7 ? 18 : 24;
  return (
    <div style={{
      flex: '1 1 130px', minWidth: 130, maxWidth: 260,
      background: 'var(--bg-card)',
      border: `1px solid ${accent}22`,
      borderRadius: 12,
      padding: '12px 16px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 12, overflow: 'hidden',
        background: `radial-gradient(ellipse at 0% 50%, ${accent}10 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
        background: `${accent}15`, border: `1px solid ${accent}28`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} color={accent} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: valueSize, fontWeight: 800, lineHeight: 1.2, color: accent,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }} title={valueStr}>{valueStr}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 3, fontWeight: 500, lineHeight: 1.25 }}>{label}</div>
        {subValue != null && (
          <div style={{
            fontSize: 11, fontWeight: 700, color: accent, marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={subValue}>{subValue}</div>
        )}
      </div>
    </div>
  );
}

// ── Condensed dashboard strip (completion banner + stat cards only) ──────────
function DashboardStrip({ appId, api, refreshKey, isAdmin, dateFilter, dateField }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const { from, to } = resolveDateRange(dateFilter);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (from) params.from = from;
    if (to)   params.to   = to;
    if (from || to) params.date_field = dateField;
    api.projectsDashboard(appId, params)
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [api, appId, refreshKey, from, to, dateField]);

  if (loading) return <div className="tl-loading"><span className="tl-spinner" /> Loading dashboard…</div>;
  if (error)   return <div className="banner banner-error">{error}</div>;
  if (!data || data.total === 0) return null;

  const active = data.active ?? Math.max(0, data.total - data.completed - data.overdue - data.near_end);
  // Per-bucket revenue (admin-only — absent entirely for non-admins).
  const rev = data.revenue;
  const subValue = (key) => (rev ? formatValue(rev[key]) : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <StatCard label="Total Projects"    value={data.total}              Icon={Briefcase}     accent="#6f7bff" subValue={subValue('total')} />
        <StatCard label="Completed"         value={data.completed}          Icon={CheckCircle2}  accent="#4ad295" subValue={subValue('completed')} />
        <StatCard label="Active"            value={active}                  Icon={TrendingUp}    accent="#22d3ee" subValue={subValue('active')} />
        <StatCard label="Overdue"           value={data.overdue}            Icon={AlertTriangle} accent="#ff6b6b" subValue={subValue('overdue')} />
        <StatCard label="Near End Date"     value={data.near_end}           Icon={Clock}         accent="#f0a93b" subValue={subValue('near_end')} />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Projects() {
  const { appId } = useParams();
  const api       = useApi();
  const { me, myRole, searchQuery } = useWorkspace();
  const isAdmin = myRole === 'super_admin' || myRole === 'admin';

  const [members, setMembers]         = useState([]);
  const [roles, setRoles]             = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [areasByRole, setAreasByRole] = useState(new Map());
  const [dashKey, setDashKey]         = useState(0);

  const [projects, setProjects]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState(['active', 'overdue', 'near_end']);
  const [ownerFilter, setOwnerFilter]     = useState('');
  const [sortBy, setSortBy]               = useState('');
  const [dateFilter, setDateFilter]       = useState({ preset: '', from: '', to: '' });
  const [dateField, setDateField]         = useState('start_date'); // 'start_date' | 'end_date'
  const [createOpen, setCreateOpen]       = useState(false);
  const [editProject, setEditProject]     = useState(null);
  const [toggleConfirm, setToggleConfirm] = useState(null);
  const [toggling, setToggling]           = useState(null);
  const [detailProject, setDetailProject] = useState(null);
  const [viewProject,   setViewProject]   = useState(null);
  const [actionError, setActionError]     = useState(null);
  const [feedModal, setFeedModal]         = useState(null);
  const [feedModalText, setFeedModalText] = useState('');
  const [feedSending, setFeedSending]     = useState(false);
  const [feedSuccess, setFeedSuccess]     = useState(null);
  const [notifying, setNotifying]         = useState(null);

  const canCreate = true; // anyone can create projects

  useEffect(() => {
    api.listAppMembers(appId)
      .then((r) => setMembers(r.items || []))
      .catch(() => {});
  }, [api, appId]);

  useEffect(() => {
    api.listRoleAssignments(appId)
      .then((r) => setRoleAssignments(r.items || []))
      .catch(() => {});
  }, [api, appId]);

  useEffect(() => {
    api.roles?.list(appId)
      .then((res) => {
        const roleRows = res?.items || [];
        setRoles(roleRows);
        return Promise.all(
          roleRows.map((role) =>
            api.listAreas(appId, role.id)
              .then((r) => ({ role, items: r?.items || [] }))
              .catch(() => ({ role, items: [] }))
          )
        );
      })
      .then((results) => {
        const map = new Map();
        (results || []).forEach(({ role, items }) => map.set(role.id, items));
        setAreasByRole(map);
      })
      .catch(() => {});
  }, [api, appId]);

  const { from: resolvedFrom, to: resolvedTo } = resolveDateRange(dateFilter);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (resolvedFrom) params.from = resolvedFrom;
      if (resolvedTo)   params.to   = resolvedTo;
      if (resolvedFrom || resolvedTo) params.date_field = dateField;
      const rows = await api.listProjects(appId, params);
      setProjects(rows);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [api, appId, search, resolvedFrom, resolvedTo, dateField]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (searchQuery !== undefined) setSearch(searchQuery); }, [searchQuery]);

  function refreshDashboard() { setDashKey((k) => k + 1); }

  const ownerOptions = useMemo(() => {
    const seen = new Map();
    for (const p of projects) {
      if (p.owner_user_id && !seen.has(p.owner_user_id)) {
        seen.set(p.owner_user_id, p.owner_name || 'Unknown');
      }
    }
    return Array.from(seen, ([value, label]) => ({ value: String(value), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);

  const filtered = useMemo(() => {
    let list = [...projects];

    if (statusFilter.length > 0) {
      list = list.filter((p) => {
        if (statusFilter.includes('inactive')) {
          if (p.computed_status === 'inactive' || p.computed_status === 'deleted') return true;
        }
        return statusFilter.includes(p.computed_status);
      });
    }
    if (ownerFilter) {
      list = list.filter((p) => String(p.owner_user_id) === ownerFilter);
    }

    if      (sortBy === 'start_date_desc') list.sort((a, b) => (b.start_date || '') > (a.start_date || '') ? 1 : -1);
    else if (sortBy === 'start_date_asc')  list.sort((a, b) => (a.start_date || '') > (b.start_date || '') ? 1 : -1);
    else if (sortBy === 'end_date_desc')   list.sort((a, b) => (b.end_date   || '') > (a.end_date   || '') ? 1 : -1);
    else if (sortBy === 'end_date_asc')    list.sort((a, b) => (a.end_date   || '') > (b.end_date   || '') ? 1 : -1);
    else if (sortBy === 'name_asc')        list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'progress_desc')   list.sort((a, b) => (b.completion_pct || 0) - (a.completion_pct || 0));
    else if (sortBy === 'progress_asc')    list.sort((a, b) => (a.completion_pct || 0) - (b.completion_pct || 0));
    else if (sortBy === 'revenue_desc')    list.sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0));
    else if (sortBy === 'revenue_asc')     list.sort((a, b) => (Number(a.revenue) || 0) - (Number(b.revenue) || 0));
    else if (sortBy === 'cost_desc')       list.sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0));
    else if (sortBy === 'cost_asc')        list.sort((a, b) => (Number(a.cost) || 0) - (Number(b.cost) || 0));
    else if (sortBy === 'profit_desc')     list.sort((a, b) => ((Number(b.revenue) || 0) - (Number(b.cost) || 0)) - ((Number(a.revenue) || 0) - (Number(a.cost) || 0)));
    else if (sortBy === 'profit_asc')      list.sort((a, b) => ((Number(a.revenue) || 0) - (Number(a.cost) || 0)) - ((Number(b.revenue) || 0) - (Number(b.cost) || 0)));
    return list;
  }, [projects, statusFilter, ownerFilter, sortBy]);

  function isOwnerOfProject(project) {
    if (!me) return false;
    return project.owner_user_id === me.id;
  }

  async function handleToggleInactive(project) {
    if (!isOwnerOfProject(project)) return;
    setActionError(null);
    setToggling(project.id);
    try {
      await api.updateProject(appId, project.id, { is_inactive: project.is_inactive ? 0 : 1 });
      setToggleConfirm(null);
      await load();
      refreshDashboard();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Failed to update project');
    } finally {
      setToggling(null);
    }
  }

  async function handleRestore(project) {
    setToggling(project.id);
    try {
      await api.updateProject(appId, project.id, { is_deleted: 0 });
      await load();
      refreshDashboard();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Failed to restore project');
    } finally {
      setToggling(null);
    }
  }

  async function handleNotify(project) {
    setNotifying(project.id);
    try {
      await api.notifyProject(appId, project.id);
      setFeedSuccess('Project update email sent to the client!');
      setTimeout(() => setFeedSuccess(null), 4000);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Failed to send notification');
    } finally {
      setNotifying(null);
    }
  }


  function openFeedModal(project) {
    setFeedModal(project);
    setFeedModalText('');
  }

  // Per-project actions shown in the project view modal's settings menu.
  // Each one closes the view modal first so its own modal / confirm / status
  // banner is visible on the listing page.
  function projectActions(project) {
    const owner      = isOwnerOfProject(project);
    const isToggling = toggling === project.id;
    const act = (fn) => () => { setViewProject(null); fn(); };

    if (project.computed_status === 'deleted') {
      return owner ? [{ label: isToggling ? 'Restoring…' : '↩️ Restore', onClick: act(() => handleRestore(project)), disabled: isToggling }] : [];
    }
    // Inactive projects are view-only — reactivating is the only action.
    if (project.is_inactive) {
      return owner ? [{ label: isToggling ? '…' : '▶️ Set Active', onClick: act(() => setToggleConfirm(project)), disabled: isToggling }] : [];
    }
    return [
      { label: '📝 Update', onClick: act(() => setDetailProject(project)) },
      ...(owner ? [
        { label: notifying === project.id ? 'Notifying…' : '✉️ Notify', onClick: act(() => handleNotify(project)), disabled: notifying === project.id },
        { label: '✏️ Edit', onClick: act(() => { setEditProject(project); setCreateOpen(true); }) },
        { label: '📢 Feed', onClick: act(() => openFeedModal(project)) },
        { label: isToggling ? '…' : '⏸️ Set Inactive', onClick: act(() => setToggleConfirm(project)), disabled: isToggling },
      ] : []),
    ];
  }

  async function handleSendFeedModal() {
    if (!feedModal || !feedModalText.trim()) return;
    const rawIds = feedModal.member_user_ids || (feedModal.members || []).map((m) => (typeof m === 'number' ? m : m.user_id));
    const memberIds = rawIds.map(Number).filter(Boolean);
    if (memberIds.length === 0) return;
    setFeedSending(true);
    try {
      await api.createPost(appId, {
        content:       textToFeedHtml(feedModalText.trim()),
        audience_type: 'users',
        audience_ids:  memberIds,
      });
      setFeedModal(null);
      setFeedSuccess(`Feed sent to ${memberIds.length} member${memberIds.length !== 1 ? 's' : ''} successfully!`);
      await load();
      refreshDashboard();
      setTimeout(() => setFeedSuccess(null), 4000);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Failed to send feed');
    } finally {
      setFeedSending(false);
    }
  }

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Projects</h1>
          <p className="tl-subtitle">Track client projects, milestones and team progress.</p>
        </div>
      </div>

      <DashboardStrip appId={appId} api={api} refreshKey={dashKey} isAdmin={isAdmin} dateFilter={dateFilter} dateField={dateField} />

      {/* Toolbar + filters */}
      <div className="tl-toolbar">
        <div className="tl-search-wrap">
          <svg className="tl-search-icon" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            className="tl-search-input"
            type="text"
            placeholder="Search Projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="tl-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <div className="tl-filters">
          <DateRangeFilterDropdown
            label="Any date"
            value={dateFilter}
            onChange={setDateFilter}
            btnClass="tl-select-btn"
            presets={PROJECT_DATE_PRESETS}
            fieldOptions={[
              { value: 'start_date', label: 'Start Date' },
              { value: 'end_date',   label: 'End Date' },
            ]}
            fieldValue={dateField}
            onFieldChange={setDateField}
          />
          <MultiFilterDropdown
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onToggle={(v) => setStatusFilter((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])}
            onClear={() => setStatusFilter([])}
            btnClass="tl-select-btn"
          />
          <SearchableSelect
            placeholder="Owner"
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={ownerOptions}
            btnClass="tl-select-btn"
          />
          <select
            className="tl-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ minWidth: 175 }}
          >
            {(isAdmin ? [...SORT_OPTIONS, ...FINANCE_SORT_OPTIONS] : SORT_OPTIONS).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {canCreate && (
            <button
              className="btn-primary"
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={() => { setEditProject(null); setCreateOpen(true); }}
            >
              + Create project
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}
      {feedSuccess && (
        <div className="banner banner-success" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>✓ {feedSuccess}</span>
          <button
            onClick={() => setFeedSuccess(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', opacity: 0.7 }}
          >×</button>
        </div>
      )}
      {actionError && (
        <div className="banner banner-error" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', opacity: 0.7 }}
          >×</button>
        </div>
      )}

      {loading ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading projects…</div>
      ) : filtered.length === 0 ? (
        <div className="tl-empty">
          <div className="tl-empty-icon">📁</div>
          <p className="tl-empty-title">
            {projects.length === 0
              ? 'No projects yet'
              : search
                ? `No projects match "${search}"`
                : 'No projects match the selected filters'}
          </p>
          <p className="tl-empty-sub">
            {projects.length === 0
              ? 'No projects have been assigned to you yet.'
              : 'Try adjusting your filters.'}
          </p>
          {canCreate && projects.length === 0 && (
            <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => { setEditProject(null); setCreateOpen(true); }}>
              + Create project
            </button>
          )}
        </div>
      ) : (
        <div className="tl-list">
          {filtered.map((project, idx) => {
            const pct        = project.completion_pct ?? 0;
            const meta       = STATUS_META[project.computed_status] || { color: '#06b6d4', bg: 'rgba(6,182,212,0.1)', border: '#06b6d4', label: project.computed_status };
            const isDone     = project.computed_status === 'completed';

            return (
              <div key={project.id} className="tl-row" style={{ cursor: 'default', alignItems: 'center', gap: 10 }}>
                {/* Serial # */}
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'var(--bg-3)', border: '1px solid var(--border)',
                  fontSize: 11, fontWeight: 700, color: 'var(--fg-dim)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {idx + 1}
                </div>

                {/* Main content — click to open project detail (tasks + milestones) */}
                <div
                  className="tl-row-body"
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => setViewProject(project)}
                >
                  <div className="tl-row-title-line">
                    <span
                      className="tl-row-title"
                      style={{ textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.55 : 1 }}
                    >
                      {project.name}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                      background: meta.bg, color: meta.color, border: `1px solid ${meta.border}30`,
                      whiteSpace: 'nowrap',
                    }}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="tl-row-chips">
                    {project.client_name && <span className="tl-chip">🏢 {project.client_name}</span>}
                    {project.owner_name  && <span className="tl-chip">👤 {project.owner_name}</span>}
                    {project.start_date  && <span className="tl-chip">Start: {formatDate(project.start_date + 'T00:00:00')}</span>}
                    {project.end_date    && <span className="tl-chip">End: {formatDate(project.end_date + 'T00:00:00')}</span>}
                    <span className="tl-chip">{project.total_tasks} task{project.total_tasks !== 1 ? 's' : ''}</span>
                    {project.members?.length > 0 && (
                      <span className="tl-chip">👥 {project.members.length} member{project.members.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>

                {/* Revenue / Cost / Profit — compact money chips. The API only
                    includes these fields for admins and the project owner,
                    so absence means hide. */}
                {'revenue' in project && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <MoneyChip
                      Icon={IndianRupee}
                      label={project.billing_type === 'per_hour' ? 'Revenue (Per Hr)' : 'Revenue (Fixed)'}
                      value={project.revenue}
                      color="#4ad295"
                    />
                    <MoneyChip Icon={Receipt}     label="Cost"    value={project.cost}    color="#f87171" />
                    <MoneyChip
                      Icon={(project.revenue - project.cost) >= 0 ? TrendingUp : TrendingDown}
                      label={(project.revenue - project.cost) >= 0 ? 'Profit' : 'Loss'}
                      value={project.revenue - project.cost}
                      color={(project.revenue - project.cost) >= 0 ? '#4ad295' : '#f87171'}
                    />
                  </div>
                )}

                {/* Progress — milestone-based, compact fixed-width bar + % (~3cm) */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                  title={`${project.completed_milestones ?? 0} of ${project.total_milestones ?? 0} milestones completed`}
                >
                  <ProgressBar pct={pct} color={meta.border} width={110} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, minWidth: 32 }}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Set Inactive / Set Active confirmation */}
      {toggleConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'var(--bg-modal)', borderRadius: 16, padding: '28px 32px',
            maxWidth: 400, width: '90%',
            border: '1px solid rgba(248,113,113,0.25)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 17, fontWeight: 700, color: 'var(--fg)' }}>
              {toggleConfirm.is_inactive ? 'Set project active?' : 'Set project inactive?'}
            </h3>
            <p style={{ margin: '0 0 22px', color: 'var(--fg-mute)', fontSize: 14, lineHeight: 1.6 }}>
              {toggleConfirm.is_inactive
                ? <>"<strong style={{ color: 'var(--fg)' }}>{toggleConfirm.name}</strong>" will become active again and show up in active views.</>
                : <>"<strong style={{ color: 'var(--fg)' }}>{toggleConfirm.name}</strong>" will be hidden from active views. All tasks and time logs are preserved, and you can set it active again anytime.</>
              }
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-secondary" onClick={() => setToggleConfirm(null)} disabled={toggling === toggleConfirm.id}>Cancel</button>
              <button
                className="btn-primary"
                style={!toggleConfirm.is_inactive ? { background: '#dc2626', borderColor: 'transparent' } : undefined}
                onClick={() => handleToggleInactive(toggleConfirm)}
                disabled={toggling === toggleConfirm.id}
              >
                {toggling === toggleConfirm.id ? 'Saving…' : (toggleConfirm.is_inactive ? 'Set Active' : 'Set Inactive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Feed Modal */}
      <Modal
        open={!!feedModal}
        title={`Send Feed — ${feedModal?.name || ''}`}
        onClose={() => { if (!feedSending) setFeedModal(null); }}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>
            This message will be sent to all project members.
          </div>
          <div>
            <textarea
              className="form-input"
              rows={6}
              value={feedModalText}
              onChange={(e) => setFeedModalText(e.target.value)}
              maxLength={2000}
              placeholder="Write your update message…"
              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, width: '100%' }}
              autoFocus
            />
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', textAlign: 'right', marginTop: 2 }}>
              {feedModalText.length}/2000
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              className="btn-secondary"
              onClick={() => setFeedModal(null)}
              disabled={feedSending}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSendFeedModal}
              disabled={feedSending || !feedModalText.trim()}
            >
              {feedSending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </Modal>

      <CreateProjectModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditProject(null); }}
        members={members}
        project={editProject}
        roles={roles}
        roleAssignments={roleAssignments}
        onSaved={async () => { await load(); refreshDashboard(); }}
      />

      <ProjectDetailModal
        open={!!detailProject}
        onClose={() => setDetailProject(null)}
        project={detailProject}
        isOwner={detailProject ? isOwnerOfProject(detailProject) : false}
        onUpdate={load}
      />

      <ProjectViewModal
        open={!!viewProject}
        onClose={() => setViewProject(null)}
        project={viewProject}
        members={members}
        roles={roles}
        areasByRole={areasByRole}
        onUpdate={async () => { await load(); refreshDashboard(); }}
        actions={viewProject ? projectActions(viewProject) : []}
      />
    </div>
  );
}
